import {
	Component,
	Input,
	OnInit,
	EventEmitter,
	Output,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	BaseClient,
	BusinessClient,
	ClientType,
	PersonClient,
} from '@models/clients';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	MatterClientType,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { ClientsState } from '@state/clients';
import {
	ClientSearchDispatch,
	ClientSearchState,
} from '@state/algolia-search/clientSearch';
import { MatterClients } from '@state/global/matter_clients';
import { AlgoliaHit } from '@state/utils/models';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { DropdownResult } from '../../atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { isGuarantor } from '@core/util/client';
import { PrefilledNewClient } from '@core/util/llc';
import { BaseComponent } from '@core/base.component';
import { TestHooks } from './matter-user-select-or-search.hooks';
import { SearchType } from 'src/app/public/leap/leap-api.service';

export enum ClientGuarantorType {
	CLIENT = 'client',
	BUYER = 'buyer',
	SELLER = 'seller',
	GUARANTOR = 'guarantor',
	BORROWER = 'borrower',
}

export enum UserSelectGroupType {
	SELECT_CURRENT = 'selectCurrent',
	SELECT_ADD = 'selectAdd',
	CREATE = 'create',
}

interface VM {
	filteredClients: BaseClient[];
	searchClients: BaseClient[];
	showDropdown: boolean;
	loading: boolean;
	searchValue: string;
	selected: BaseClient;
	selectedName: string;
	disabled: boolean;
	prefilledNewClient: PrefilledNewClient;
	title: string;
	placeholder: string;
	noResultsMessage: string;
	clientId: string;
}

const defaultVM: VM = {
	filteredClients: [],
	searchClients: [],
	showDropdown: false,
	loading: false,
	searchValue: '',
	selected: null,
	selectedName: null,
	disabled: false,
	prefilledNewClient: null,
	title: 'Existing clients on file',
	placeholder: 'Select or search client',
	noResultsMessage: 'No clients found',
	clientId: null,
};

@UntilDestroy()
@Component({
	selector: 'app-matter-user-select-or-search',
	templateUrl: './matter-user-select-or-search.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MatterUserSelectOrSearchComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	public currentClients: BaseClient[];

	public clientTypes = ClientType;
	public ICON = ICON;
	public userSelectGroupType = UserSelectGroupType;

	private _selectedId: string;
	private _filterIds: string[] = [];
	private _filterByParent: string;
	@Input() set disabled(disabled: false) {
		this.VM = { disabled };
	}

	@Input() set selectedId(selectedId: string) {
		if (this._selectedId !== selectedId) {
			this.VM = selectedId
				? { clientId: selectedId }
				: { selected: null, selectedName: null };
		}
		this._selectedId = selectedId;
	}

	@Input() set title(title: string) {
		if (title) {
			this.VM = { title };
		}
	}
	@Input() set placeholder(placeholder: string) {
		if (placeholder) {
			this.VM = { placeholder };
		}
	}
	@Input() set noResultsMessage(noResultsMessage: string) {
		if (noResultsMessage) {
			this.VM = { noResultsMessage };
		}
	}

	@Input() set prefilledNewClient(prefilledNewClient: PrefilledNewClient) {
		this.VM = { prefilledNewClient };
	}
	@Input() displayClientTypes: ClientType[] = [
		ClientType.PERSON,
		ClientType.BUILDER,
		ClientType.CORPORATION,
	];
	@Input() showMatterClientTypes: ClientGuarantorType[] = [
		ClientGuarantorType.CLIENT,
		ClientGuarantorType.GUARANTOR,
	];
	@Input()
	set filterIds(filterIds: string[]) {
		this._filterIds = filterIds;
		this.filterCurrentClients();
	}
	@Input()
	set filterByParent(filterByParent: string) {
		this._filterByParent = filterByParent;
		this.filterCurrentClients();
	}
	@Output() selectedCurrent: EventEmitter<BaseClient> =
		new EventEmitter<BaseClient>();
	@Output() selectedAddClient: EventEmitter<BaseClient> =
		new EventEmitter<BaseClient>();
	@Output() selectedCreateClient: EventEmitter<string> =
		new EventEmitter<string>();
	@Output() remove: EventEmitter<BaseClient> = new EventEmitter<BaseClient>();
	@ViewChild('createTemplate') createTemplate: HTMLElement;

	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	clients$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@Select(MatterClients.get_otherPartyWithAssociationMapped)
	otherParty$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@Select(ClientSearchState.list) searchClients$: Observable<
		AlgoliaHit<BaseClient>[]
	>;

	constructor(
		private clientSearchDispatch: ClientSearchDispatch,
		private clientState: ClientsState,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.clientSearchDispatch.clearResults();
		combineLatest([this.clients$, this.otherParty$])
			.pipe(
				untilDestroyed(this),
				map(([clients, otherParty]) => {
					const _clients = cloneDeep(clients);
					const _otherParty = cloneDeep(otherParty);
					let allClients = _clients.concat(_otherParty);

					if (this.displayClientTypes) {
						allClients = allClients.filter(
							(x: PersonMatterClient | BusinessMatterClient) =>
								this.displayClientTypes?.includes(x.type),
						);
					}

					if (this.showMatterClientTypes) {
						allClients = allClients
							.filter((x: PersonMatterClient | BusinessMatterClient) => {
								let filter = false;
								if (
									this.showMatterClientTypes?.includes(
										ClientGuarantorType.CLIENT,
									)
								) {
									filter = x.client === true && !isGuarantor(x);
								}

								if (
									!filter &&
									this.showMatterClientTypes?.includes(
										ClientGuarantorType.GUARANTOR,
									)
								) {
									filter = isGuarantor(x);
								}

								if (
									!filter &&
									this.showMatterClientTypes?.includes(
										ClientGuarantorType.SELLER,
									)
								) {
									filter = x?.clientTypes?.includes(MatterClientType.SELLER);
								}

								if (
									!filter &&
									this.showMatterClientTypes?.includes(
										ClientGuarantorType.BUYER,
									)
								) {
									filter = x?.clientTypes?.includes(MatterClientType.BUYER);
								}

								if (
									!filter &&
									this.showMatterClientTypes?.includes(
										ClientGuarantorType.BORROWER,
									)
								) {
									filter = x?.clientTypes?.includes(MatterClientType.BORROWER);
								}

								return filter;
							})
							?.filter(
								(value, index, self) =>
									self.findIndex((x) => x.id === value.id) === index,
							);
					}

					const _clientsWithSigningOfficers: (PersonClient | BusinessClient)[] =
						cloneDeep(allClients);

					allClients.forEach((x) => {
						if (x.type !== ClientType.PERSON) {
							x.clientAssociations.forEach((association) => {
								if (association?.associations?.types?.signing_officer) {
									_clientsWithSigningOfficers.push(association.client);
								}
							});
						}
					});

					return _clientsWithSigningOfficers;
				}),
			)
			.subscribe(
				(
					clients: (
						| PersonMatterClientWithAssociations
						| BusinessMatterClientWithAssociations
					)[],
				) => {
					this.currentClients = clients;
					this.filterCurrentClients();
				},
			);
		this.searchClients$
			.pipe(
				untilDestroyed(this),
				map((clients: AlgoliaHit<BaseClient>[]) => {
					if (this.displayClientTypes) {
						return clients
							?.filter((x: BaseClient) =>
								this.displayClientTypes?.includes(x.type),
							)
							.filter((x) => !this._filterIds.includes(x.id));
					}

					return clients;
				}),
			)
			.subscribe((clients: BaseClient[]) => {
				this.VM = { loading: false, searchClients: clients };
			});

		this.vm$
			.pipe(
				untilDestroyed(this),
				map((vm) => vm.clientId),
				filter((clientId) => !!clientId),
				distinctUntilChanged(),
				switchMap((clientId) => this.clientState.repo.Get$(clientId)),
			)
			.subscribe((client) => {
				this.VM = { selected: client, selectedName: this.selectedName(client) };
			});
	}

	onAddSelect($event: DropdownResult<BaseClient>): void {
		// validate that the object returned is of type baseClient
		if (
			Object.prototype.hasOwnProperty.call($event.result, 'id') &&
			$event.groupType === UserSelectGroupType.SELECT_ADD
		) {
			this.selectedAddClient.emit($event.result);
		}

		this.VM = { showDropdown: false };
	}

	onCurrentSelect($event: DropdownResult<BaseClient>): void {
		// validate that the object returned is of type baseClient
		if (
			Object.prototype.hasOwnProperty.call($event.result, 'id') &&
			$event.groupType === UserSelectGroupType.SELECT_CURRENT
		) {
			// if group === 1 then its a search result
			this.selectedCurrent.emit($event.result);
		}

		this.VM = { showDropdown: false };
	}

	create() {
		if (this.VM.prefilledNewClient) {
			const name =
				this.VM.prefilledNewClient.type === ClientType.PERSON
					? `${this.VM.prefilledNewClient.firstName} ${this.VM.prefilledNewClient.lastName}`
					: this.VM.prefilledNewClient.name;
			this.selectedCreateClient.emit(name);
		} else {
			this.selectedCreateClient.emit(this.VM.searchValue);
		}
	}

	_selectedCreateClient(): void {
		this.selectedCreateClient.emit();
	}

	toggleShowDropdown() {
		this.VM = { showDropdown: !this.VM.showDropdown };
	}

	debounce(searchValue: string) {
		this.VM = { loading: true };

		if (!this.VM.showDropdown) {
			this.VM = { showDropdown: true };
		}
		if (searchValue.length > 0) {
			this.clientSearchDispatch.search(searchValue, SearchType.CardsAndPeople);
		}
	}

	onRemove() {
		this.remove.emit(this.VM.selected);
	}

	private selectedName(selected: BaseClient): string {
		if (selected?.type === ClientType.PERSON) {
			let result = '';
			if ((selected as PersonClient)?.firstName) {
				result += `${(selected as PersonClient).firstName} `;
			}
			if ((selected as PersonClient)?.middleNames) {
				result += `${(selected as PersonClient).middleNames} `;
			}
			if ((selected as PersonClient)?.lastName) {
				result += (selected as PersonClient).lastName;
			}

			return result.trim();
		}

		return (selected as BusinessClient)?.name;
	}

	private filterCurrentClients(): void {
		if (this.currentClients) {
			let filteredClients = this.currentClients.filter(
				(client) => !this._filterIds.includes(client.id),
			);
			if (this._filterByParent) {
				const parent = this.currentClients.find(
					(client) => client.id === this._filterByParent,
				);
				if (parent) {
					const associationIds = Object.keys(parent.associations ?? {});
					filteredClients = associationIds.length
						? filteredClients.filter((client) =>
								associationIds.includes(client.id),
							)
						: [];
				}
			}
			this.VM = { filteredClients };
		}
	}
}
